import { useEffect, useState } from "react";
import { Disc3, ExternalLink } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { PlatformCarousel } from "@/components/PlatformCarousel";
import { UntitledEmbed } from "@/components/UntitledEmbed";
import { SoundcloudEmbed } from "@/components/SoundcloudEmbed";

export default function Catalog() {
  const [entries, setEntries] = useState([]);

  useEffect(() => {
    api.get("/catalog").then((r) => setEntries(r.data)).catch(() => {});
  }, []);

  return (
    <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-20 lg:px-8" data-testid="catalog-page">
      <Reveal>
        <p className="font-mono text-xs uppercase tracking-widest text-emerald-300">Placements & credits</p>
        <h1 className="font-heading mt-3 text-4xl font-extrabold tracking-tight text-white sm:text-5xl">Catalog</h1>
        <p className="mt-4 max-w-xl text-sm text-zinc-400 sm:text-base">
          Records produced, mixed and engineered at the lab.
        </p>
        <a
          href="https://open.spotify.com/artist/1fVSxNbesvw20EEMIUc4OU"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="catalog-spotify-link"
          className="mt-6 inline-flex items-center gap-3 rounded-full border border-emerald-300/40 px-6 py-3 text-xs font-bold uppercase tracking-wider text-emerald-200 transition-colors hover:bg-emerald-300 hover:text-black"
        >
          <Disc3 size={15} /> Listen on Spotify
        </a>
      </Reveal>

      <Reveal delay={0.1}>
        <UntitledEmbed />
      </Reveal>

      <Reveal delay={0.12}>
        <SoundcloudEmbed />
      </Reveal>

      <Reveal delay={0.15}>
        <div className="mt-10" data-testid="platform-carousel-section">
          <p className="mb-4 font-mono text-xs uppercase tracking-widest text-emerald-300">Stream everywhere</p>
          <PlatformCarousel />
        </div>
      </Reveal>

      <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="catalog-grid">
        {entries.map((e, i) => (<Reveal key={e.id} delay={(i % 3) * 0.08}>
            <div className="group overflow-hidden rounded-2xl border border-white/10 bg-[#141419] transition-colors hover:border-white/25" data-testid={`catalog-entry-${e.id}`}>
              <div className="relative aspect-square overflow-hidden">
                <img src={fileUrl(e.cover_url)} alt={e.title} loading="lazy" className="spotlight-img h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                <span className="absolute right-3 top-3 rounded-full bg-black/60 px-3 py-1 font-mono text-[11px] text-emerald-200 backdrop-blur-md">{e.year}</span>
              </div>
              <div className="p-5">
                <h3 className="font-heading text-lg font-bold text-white">{e.title}</h3>
                <p className="mt-1 text-sm text-zinc-400">{e.artist}</p>
                <div className="mt-3 flex items-center justify-between">
                  <span className="font-mono text-[11px] uppercase tracking-wider text-emerald-300">{e.role}</span>
                  <span className="text-xs text-zinc-500">{e.streams}</span>
                </div>
                {e.link && (
                  <a href={e.link} target="_blank" rel="noopener noreferrer" data-testid={`catalog-listen-${e.id}`}
                    className="mt-4 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-emerald-200 hover:text-emerald-200">
                    Listen <ExternalLink size={12} />
                  </a>
                )}
              </div>
            </div>
          </Reveal>
        ))}
      </div>
      {entries.length === 0 && (
        <p className="mt-12 rounded-2xl border border-dashed border-white/10 p-10 text-center text-sm text-zinc-600" data-testid="catalog-empty">
          Placements are being updated — check back soon.
        </p>
      )}
    </div>
  );
}
